"use client";
import { useFilters } from "@/hooks";
import {
  BuilderProps,
  IndexCard,
  SortingFunction,
  SortingObject,
} from "@/types";
import { Filters, TreeNode } from "@/types/General";
import {
  createContext,
  FC,
  PropsWithChildren,
  useState,
  useEffect,
  useCallback,
  Suspense,
} from "react";
import { useTransition } from "react";
import {
  createTreeFilter,
  retrieveIndexCards,
  getSearchFieldsByVariant,
} from "@/helpers/api/builder";
import { usePathname } from "next/navigation";
import { createSortingMapping, sortCards } from "@/helpers";
import extractLocaleAndUrl from "@/helpers/extractLocaleAndUrl";
import { Spinner } from "@/icons";

export type SortDescriptor = {
  field: string;
  order: "ascending" | "descending";
};

type IndexCardsContextType = {
  cards: { id: string; data: IndexCard }[];
  displayedCards: { id: string; data: IndexCard }[];
  hiddenCards: { id: string; data: IndexCard }[];
  filters: {
    label: string;
    name: string;
    categories: TreeNode[];
  }[];
  selectedFilters: Filters;
  isLoading: boolean;
  numberOfResults: number;
  page: number;
  numberOfPaginatedCards: number;
  searchExpression: string;
  activeSortDescriptor: SortDescriptor | null;
  onGoToFirstPage: () => void;
  onGoToLastPage: () => void;
  onGoToNextPage: () => void;
  onFilter: (newFilters: Filters) => void;
  onSort: (
    sortingFn: SortingFunction | null,
    descriptor?: SortDescriptor | null,
  ) => void;
};
export const IndexCardsContext = createContext<IndexCardsContextType>({
  cards: [],
  displayedCards: [],
  hiddenCards: [],
  filters: [],
  selectedFilters: [],
  isLoading: false,
  numberOfResults: 0,
  numberOfPaginatedCards: 10,
  page: 1,
  searchExpression: "",
  activeSortDescriptor: null,
  onGoToFirstPage: () => {},
  onGoToLastPage: () => {},
  onGoToNextPage: () => {},
  onFilter: () => {},
  onSort: () => {},
});

export type IndexCardsContextBlockProps = {
  paginationType: "infinite" | "expandable";
  numberOfPaginatedCards?: number;
  initialSorting: SortingObject;
  cards: { id: string; data: IndexCard }[];
  numberOfResults: number;
  filters: { id: string; data: IndexCard }[];
  searchExpression?: string;
  areAllCardsInMarkup: boolean;
  variant:
    | "Resource"
    | "Event"
    // NOT YET IMPLEMENTED
    | "Use Case"
    | "Integration"
    | "Product"
    | "Newsroom"
    | "CVE";
} & Pick<BuilderProps, "builderContext" | "builderBlock">;
export const IndexCardsContextBlock: FC<
  PropsWithChildren<IndexCardsContextBlockProps>
> = ({
  children,
  cards: initialCards,
  builderContext,
  builderBlock,
  numberOfPaginatedCards = 10,
  initialSorting,
  filters,
  numberOfResults,
  searchExpression,
  areAllCardsInMarkup = true,
  variant,
}) => {
  const searchFields = getSearchFieldsByVariant(variant);
  const { url: path } = extractLocaleAndUrl(
    usePathname().split("/").filter(Boolean),
  );

  const locale = builderContext.rootState.locale as string;

  const initialSortingObject = createSortingMapping([initialSorting]) ?? null;
  let initialSortingFunction =
    initialSortingObject?.[0] ?? ((() => 0) as SortingFunction);

  // In case a bound function is used, we need to create the function from string. Source is Builder so we can trust the input.
  if (builderBlock.bindings?.["component.options.initialSorting.callback"]) {
    const initialSortingCallbackBinding =
      builderBlock.bindings["component.options.initialSorting.callback"];

    try {
      initialSortingFunction = Function(
        initialSortingCallbackBinding,
      ) as SortingFunction;
    } catch (error) {
      const reason = error instanceof Error ? error.message : "Unknown error";
      throw new Error(
        `Failed to create sorting function for binding "component.options.initialSorting.callback": ${reason}`,
      );
    }
  }
  // this state stores the sorting function inside an object with the `sortingCallback` prop
  const [sortingObject, setSortingObject] = useState<Record<
    "sortingCallback",
    SortingFunction
  > | null>({ sortingCallback: initialSortingFunction });
  const [cards, setCards] = useState(
    sortCards(initialCards, initialSortingFunction),
  );

  const sortedCards = sortCards(initialCards, sortingObject?.sortingCallback);

  const initialDescriptor: SortDescriptor | null =
    initialSorting && "field" in initialSorting && "order" in initialSorting
      ? { field: initialSorting.field, order: initialSorting.order }
      : null;
  const [activeSortDescriptor, setActiveSortDescriptor] =
    useState<SortDescriptor | null>(initialDescriptor);
  const [page, setPage] = useState(1);

  const { selectedFilters, setSelectedFilters } = useFilters([]);
  const [totalNumberOfCards, setTotalNumberOfCards] = useState(numberOfResults);

  const [isLoading, startLoadingTransition] = useTransition();

  const [displayedCards, setDisplayedCards] = useState(
    sortedCards?.slice(0, page * numberOfPaginatedCards),
  );
  const [hiddenCards, setHiddenCards] = useState(
    areAllCardsInMarkup
      ? sortedCards?.slice(page * numberOfPaginatedCards)
      : [],
  );

  const onQuery = useCallback(
    (filters: Filters) => {
      setPage(1);
      setCards([]);
      setDisplayedCards([]);
      setHiddenCards([]);

      startLoadingTransition(async () => {
        const { cards, numberOfResults } = await retrieveIndexCards(
          path,
          locale,
          filters,
          searchExpression,
          undefined,
          searchFields,
        );
        const sortedCards = sortCards(cards, sortingObject?.sortingCallback);

        startLoadingTransition(() => {
          setCards(sortedCards);
          setDisplayedCards(sortedCards.slice(0, numberOfPaginatedCards));
          if (areAllCardsInMarkup) {
            setHiddenCards(sortedCards.slice(numberOfPaginatedCards));
          }
          setTotalNumberOfCards(numberOfResults);
        });
      });
    },
    [
      path,
      locale,
      searchExpression,
      sortingObject,
      numberOfPaginatedCards,
      areAllCardsInMarkup,
      searchFields,
      setPage,
      setCards,
      setDisplayedCards,
      setHiddenCards,
      setTotalNumberOfCards,
      startLoadingTransition,
    ],
  );

  useEffect(() => {
    if (searchExpression != null) {
      // We intentionally refresh index results when incoming search term and URL differ.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      onQuery(selectedFilters);
    }
  }, [selectedFilters, onQuery, searchExpression]);

  const onGoToFirstPage = () => {
    setPage(1);
    const allSorted = [...displayedCards, ...hiddenCards];
    if (allSorted.length > 0) {
      setDisplayedCards(allSorted.slice(0, numberOfPaginatedCards));

      if (areAllCardsInMarkup) {
        setHiddenCards(allSorted.slice(numberOfPaginatedCards));
      }
    }
  };

  const onGoToLastPage = () => {
    const allSorted = [...displayedCards, ...hiddenCards];
    const lastPage = Math.ceil(numberOfResults / numberOfPaginatedCards);
    setPage(lastPage);
    setDisplayedCards(allSorted.slice(0, lastPage * numberOfPaginatedCards));

    if (areAllCardsInMarkup) {
      setHiddenCards(allSorted.slice(lastPage * numberOfPaginatedCards));
    }
  };

  const onGoToNextPage = () => {
    const nextBatch = sortedCards.slice(
      page * numberOfPaginatedCards,
      (page + 1) * numberOfPaginatedCards,
    );

    setPage((currentValue) => currentValue + 1);
    setDisplayedCards([...displayedCards, ...nextBatch]);

    if (areAllCardsInMarkup) {
      setHiddenCards(hiddenCards.slice(numberOfPaginatedCards));
    }
  };

  const onFilter = (newFilters: Filters) => {
    setSelectedFilters(newFilters);
    onQuery(newFilters);
  };

  const onSort = (
    sortingFn: SortingFunction | null,
    descriptor?: SortDescriptor | null,
  ) => {
    const sortedCards = sortCards(cards, sortingFn);
    setDisplayedCards(sortedCards.slice(0, page * numberOfPaginatedCards));
    if (areAllCardsInMarkup) {
      setHiddenCards(sortedCards.slice(page * numberOfPaginatedCards));
    }
    setSortingObject(sortingFn ? { sortingCallback: sortingFn } : null);
    setActiveSortDescriptor(descriptor ?? null);
  };

  return (
    <Suspense
      fallback={
        <div className="mx-auto mt-8 w-fit animate-spin">
          <Spinner />
        </div>
      }
    >
      <IndexCardsContext
        value={{
          cards,
          displayedCards,
          hiddenCards,
          numberOfResults: totalNumberOfCards,
          filters: createTreeFilter(filters),
          selectedFilters,
          isLoading,
          numberOfPaginatedCards,
          page,
          searchExpression: searchExpression ?? "",
          activeSortDescriptor,
          onGoToFirstPage,
          onGoToLastPage,
          onGoToNextPage,
          onFilter,
          onSort,
        }}
      >
        {children}
      </IndexCardsContext>
    </Suspense>
  );
};
